import { Map } from 'immutable';

import { Entity } from './interfaces/Entity';
import { StringMap } from './interfaces/StringMap';

export function findEntity<T extends Entity>(array: T[], key: string) {
  return array.find((arrayItem) => arrayItem.uuid === key);
}

export function getEntity<T extends Entity>(array: T[], key: string) {
  const entity = findEntity(array, key);
  if (!entity) throw new Error(`Cannot find entity with key ${key}`);
  return entity;
}

export function getEntityFromMap<Key, Value>(map: Map<Key, Value>, key: Key) {
  const entity = map.get(key);
  if (!entity) throw new Error(`Cannot find entity with key ${key}`);
  return entity;
}

export function addEntity<T extends Entity>(array: T[], item: T) {
  return [...array, item];
}

export function replaceEntity<T extends Entity>(array: T[], item: T) {
  return array.map((arrayItem) => (arrayItem.uuid === item.uuid ? item : arrayItem));
}

export function upsertEntity<T extends Entity>(array: T[], item: T): T[] {
  return findEntity(array, item.uuid) ? replaceEntity(array, item) : addEntity(array, item);
}

export function partialReplaceEntity<T extends Entity>(array: T[], item: Partial<T> & Entity) {
  return array.map((arrayItem) => (arrayItem.uuid === item.uuid ? { ...arrayItem, ...item } : arrayItem));
}

export function lastEntity<T extends Entity>(array: T[]) {
  return array.length > 0 ? array[array.length - 1] : null;
}

export function toIds<T extends Entity>(array: T[]) {
  return array.map((arrayItem) => arrayItem.uuid);
}

export function union<T extends Entity>(array1: T[], array2: T[]) {
  return [...array1.filter((item) => !array2.some(({ uuid }) => item.uuid === uuid)), ...array2];
}

export function intersect<T>(array1: T[], array2: T[]): boolean {
  return array1.some((array1Item) => array2.some((array2Item) => array1Item === array2Item));
}

export function valuesFromMap<T extends Entity>(object: StringMap<T>): T[] {
  if (!object) return [];
  return Object.keys(object).map((key) => object[key]);
}
